import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Filter, Grid, List, ChevronDown, ArrowLeft, Package } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useCategories } from '../hooks/useCategories';
import { useProducts } from '../hooks/useProducts';

const DynamicCategoryPage = () => {
  const { categorySlug } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const subcategoryParam = searchParams.get('subcategory');
  
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [selectedSubcategory, setSelectedSubcategory] = useState(subcategoryParam || 'all');
  
  const { categories, loading: categoriesLoading } = useCategories();
  const { products, loading: productsLoading } = useProducts();
  
  const [categoryData, setCategoryData] = useState<any>(null);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  
  // Find the current category data
  useEffect(() => {
    if (!categoriesLoading && categories.length > 0 && categorySlug) {
      const category = categories.find(c => c.slug === categorySlug);
      if (category) {
        setCategoryData(category);
        setSubcategories(category.subcategories || []);
      }
    }
  }, [categories, categoriesLoading, categorySlug]);
  
  // Update selected subcategory when URL changes
  useEffect(() => {
    setSelectedSubcategory(subcategoryParam || 'all');
  }, [subcategoryParam]);
  
  // Filter products based on category, subcategory, price, and sort
  useEffect(() => {
    if (productsLoading || !products.length || !categorySlug) return;
    
    // Filter by category
    let filtered = products.filter(product => {
      const productCategorySlug = product.category_name?.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
      return productCategorySlug === categorySlug;
    });
    
    // Filter by subcategory if selected
    if (selectedSubcategory !== 'all') {
      filtered = filtered.filter(product => {
        const productSubcategorySlug = product.subcategory_name?.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
        return productSubcategorySlug === selectedSubcategory;
      });
    }
    
    // Filter by price range
    if (priceRange !== 'all') {
      const [min, max] = priceRange.split('-').map(Number);
      if (priceRange.endsWith('+')) {
        filtered = filtered.filter(p => p.price > min);
      } else {
        filtered = filtered.filter(p => p.price >= min && p.price <= max);
      }
    }
    
    // Sort products
    if (sortBy === 'price-low') {
      filtered = [...filtered].sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered = [...filtered].sort((a, b) => b.price - a.price);
    } else if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'newest') {
      filtered = [...filtered].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }
    
    setFilteredProducts(filtered);
  }, [products, productsLoading, categorySlug, selectedSubcategory, priceRange, sortBy]);
  
  // Get appropriate price ranges based on category
  const getPriceRanges = () => {
    switch (categorySlug) {
      case 'furniture':
        return [
          { value: 'all', label: 'All Prices' },
          { value: '0-100', label: '$0 - $100' },
          { value: '100-300', label: '$100 - $300' },
          { value: '300-500', label: '$300 - $500' },
          { value: '500+', label: '$500+' }
        ];
      case 'manpower':
        return [
          { value: 'all', label: 'All Prices' },
          { value: '0-15', label: '$0 - $15/hr' },
          { value: '15-25', label: '$15 - $25/hr' },
          { value: '25-50', label: '$25 - $50/hr' },
          { value: '50+', label: '$50+/hr' }
        ];
      case 'tourism':
        return [
          { value: 'all', label: 'All Prices' },
          { value: '0-200', label: '$0 - $200' },
          { value: '200-400', label: '$200 - $400' },
          { value: '400-600', label: '$400 - $600' },
          { value: '600+', label: '$600+' }
        ];
      default:
        return [
          { value: 'all', label: 'All Prices' },
          { value: '0-25', label: '$0 - $25' },
          { value: '25-50', label: '$25 - $50' },
          { value: '50-100', label: '$50 - $100' },
          { value: '100+', label: '$100+' }
        ];
    }
  };
  
  // Get hero banner image based on category
  const getHeroBannerImage = () => {
    switch (categorySlug) {
      case 'furniture':
        return 'https://images.pexels.com/photos/1350789/pexels-photo-1350789.jpeg';
      case 'manpower':
        return 'https://images.pexels.com/photos/8961251/pexels-photo-8961251.jpeg';
      case 'tourism':
        return 'https://images.pexels.com/photos/3155666/pexels-photo-3155666.jpeg';
      case 'freelancer':
        return 'https://images.pexels.com/photos/574071/pexels-photo-574071.jpeg';
      case 'garments-apparel':
        return 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg';
      case 'jute-natural-fiber':
        return 'https://images.pexels.com/photos/5217959/pexels-photo-5217959.jpeg';
      case 'frozen-foods':
        return 'https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg';
      case 'leather-goods':
        return 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg';
      case 'pharmaceuticals-health':
        return 'https://images.pexels.com/photos/3683074/pexels-photo-3683074.jpeg';
      case 'scrap-recycling':
        return 'https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg';
      default:
        return 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg';
    }
  };
  
  // Get gradient colors based on category
  const getGradientColors = () => {
    switch (categorySlug) {
      case 'furniture':
        return 'from-amber-500/80 to-yellow-700/80';
      case 'manpower':
        return 'from-purple-500/80 to-indigo-700/80';
      case 'tourism':
        return 'from-teal-500/80 to-emerald-700/80';
      case 'freelancer':
        return 'from-blue-500/80 to-violet-700/80';
      case 'garments-apparel':
        return 'from-blue-500/80 to-indigo-700/80';
      case 'jute-natural-fiber':
        return 'from-green-500/80 to-emerald-700/80';
      case 'frozen-foods':
        return 'from-cyan-500/80 to-blue-700/80';
      case 'leather-goods':
        return 'from-amber-500/80 to-orange-700/80';
      case 'pharmaceuticals-health':
        return 'from-red-500/80 to-rose-700/80';
      case 'scrap-recycling':
        return 'from-lime-500/80 to-green-700/80';
      default:
        return 'from-orange-500/80 to-red-700/80';
    }
  };
  
  // Get primary color for UI elements based on category
  const getPrimaryColor = () => {
    switch (categorySlug) {
      case 'furniture':
        return 'amber-600';
      case 'manpower':
        return 'purple-600';
      case 'tourism':
        return 'teal-600';
      case 'freelancer':
        return 'blue-600';
      case 'garments-apparel':
        return 'blue-600';
      case 'jute-natural-fiber':
        return 'green-600';
      case 'frozen-foods':
        return 'cyan-600';
      case 'leather-goods':
        return 'amber-600';
      case 'pharmaceuticals-health':
        return 'red-600';
      case 'scrap-recycling':
        return 'lime-600';
      default:
        return 'orange-600';
    }
  };
  
  // Format category name for display
  const formatCategoryName = (slug: string | undefined) => {
    if (!slug) return '';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .replace('And', '&');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Back Button */}
        <div className="flex items-center mb-8">
          <Link to="/" className="mr-4 text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {categoryData?.name || formatCategoryName(categorySlug)}
            </h1>
            <p className="text-gray-600">
              {categoryData?.description || `High-quality ${formatCategoryName(categorySlug)} products from Bangladesh`}
            </p>
          </div>
        </div>

        {/* Hero Banner */}
        <div className="relative rounded-xl overflow-hidden mb-8 h-64 md:h-80">
          <img 
            src={getHeroBannerImage()} 
            alt={categoryData?.name || formatCategoryName(categorySlug)} 
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${getGradientColors()} flex flex-col justify-center p-8`}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {categoryData?.name || formatCategoryName(categorySlug)}
            </h2>
            <p className="text-white text-lg md:text-xl max-w-lg">
              {categoryData?.description || `Discover high-quality ${formatCategoryName(categorySlug)} products made in Bangladesh`}
            </p>
          </div>
        </div>

        {/* Filters and Controls */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            {/* Filter Button */}
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors lg:hidden"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </button>

            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center space-x-6">
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
                className={`border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-${getPrimaryColor()}`}
              >
                <option value="all">All {categoryData?.name || formatCategoryName(categorySlug)}</option>
                {subcategories.map((subcategory) => (
                  <option key={subcategory.id} value={subcategory.slug}>
                    {subcategory.name}
                  </option>
                ))}
              </select>

              <select
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className={`border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-${getPrimaryColor()}`}
              >
                {getPriceRanges().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort and View Controls */}
            <div className="flex items-center space-x-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-${getPrimaryColor()}`}
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest</option>
              </select>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? `bg-${getPrimaryColor()} text-white` : 'bg-gray-100 text-gray-600'}`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? `bg-${getPrimaryColor()} text-white` : 'bg-gray-100 text-gray-600'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Filters */}
          {isFilterOpen && (
            <div className="lg:hidden mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 gap-4">
                <select
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  className={`border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-${getPrimaryColor()}`}
                >
                  <option value="all">All {categoryData?.name || formatCategoryName(categorySlug)}</option>
                  {subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.slug}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>

                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className={`border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-${getPrimaryColor()}`}
                >
                  {getPriceRanges().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Info */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredProducts.length} products
            {selectedSubcategory !== 'all' && subcategories.length > 0 && (
              <span className={`ml-2 text-${getPrimaryColor()} font-medium`}>
                in {subcategories.find(c => c.slug === selectedSubcategory)?.name || selectedSubcategory}
              </span>
            )}
          </p>
        </div>

        {/* Products Grid */}
        {productsLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 border-${getPrimaryColor()}`}></div>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className={`grid gap-4 md:gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
              : 'grid-cols-1'
          }`}>
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your filters or search criteria to find more products.
            </p>
            <button 
              onClick={() => {
                setSelectedSubcategory('all');
                setPriceRange('all');
                setSortBy('featured');
              }}
              className={`bg-${getPrimaryColor()} text-white px-6 py-2 rounded-lg hover:bg-${getPrimaryColor().replace('600', '700')} transition-colors`}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicCategoryPage;